import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Bell, PaperPlaneTilt, Eye, Clock, CheckCircle, XCircle } from '@phosphor-icons/react';
import api, { getErrorMessage } from '../utils/api';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { EditRequestsPanel } from '../components/EditRequests';

const Notifications = () => {
  const { activeBranch } = useBranch();
  const [customers, setCustomers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [templateType, setTemplateType] = useState('batch_reminder');
  const [previewMessage, setPreviewMessage] = useState('');
  const [previewPhone, setPreviewPhone] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, batchesRes, notificationsRes] = await Promise.all([
        api.get('/customers'),
        api.get('/batches'),
        api.get('/notifications?limit=20')
      ]);
      setCustomers(customersRes.data);
      setBatches(batchesRes.data);
      setNotifications(notificationsRes.data.notifications);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handlePreview = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setLoading(true);
    try {
      const selectedBatchData = batches.find(b => b.batch_id === selectedBatch);
      
      const response = await api.post('/notifications/preview', {
        template_type: templateType,
        customer_id: selectedCustomer,
        batch_date: selectedBatchData?.date,
        batch_time: `${selectedBatchData?.start_time} - ${selectedBatchData?.end_time}`
      });

      setPreviewMessage(response.data.message);
      setPreviewPhone(response.data.phone_number);
      toast.success('Preview generated');
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setLoading(true);
    try {
      const selectedBatchData = batches.find(b => b.batch_id === selectedBatch);
      
      const response = await api.post('/notifications/send', {
        template_type: templateType,
        customer_id: selectedCustomer,
        batch_date: selectedBatchData?.date,
        batch_time: `${selectedBatchData?.start_time} - ${selectedBatchData?.end_time}`
      });

      toast.success('Notification queued successfully!');
      fetchData();
      setPreviewMessage('');
      setSelectedCustomer('');
      setSelectedBatch('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send notification'));
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSend = async () => {
    if (!selectedBatch) {
      toast.error('Please select a batch');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/notifications/batch?batch_id=${selectedBatch}&template_type=${templateType}`);
      toast.success(`Sent ${response.data.notifications_sent} notifications!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to send batch notifications');
    } finally {
      setLoading(false);
    }
  };

  const selectedCustomerData = customers.find(c => c.customer_id === selectedCustomer);

  return (
    <Layout>
      <div data-testid="notifications-page" className="space-y-6">
        <div>
          <h1 className="text-3xl font-light text-[#1C1C1A] mb-2">Notifications</h1>
          <p className="text-[#1C1C1A]/60">Send WhatsApp reminders and updates to customers</p>
        </div>

      <EditRequestsPanel branchId={activeBranch?.branch_id} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Send Notification Card */}
        <Card className="p-6 bg-white border-[#E5E5DF]" data-testid="send-notification-card">
          <div className="flex items-center gap-2 mb-4">
            <PaperPlaneTilt className="w-5 h-5 text-[#2C4C3B]" />
            <h2 className="text-xl font-medium text-[#1C1C1A]">Send Notification</h2>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Customer</Label>
              <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                <SelectTrigger data-testid="customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(customer => (
                    <SelectItem key={customer.customer_id} value={customer.customer_id}>
                      {customer.name} - {customer.phone_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Template Type</Label>
              <Select value={templateType} onValueChange={setTemplateType}>
                <SelectTrigger data-testid="template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="batch_reminder">Batch Reminder</SelectItem>
                  <SelectItem value="follow_up">Follow-up Message</SelectItem>
                  <SelectItem value="purchase_reminder">Purchase Reminder</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {templateType === 'batch_reminder' && (
              <div>
                <Label>Batch</Label>
                <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                  <SelectTrigger data-testid="batch-select">
                    <SelectValue placeholder="Select batch" />
                  </SelectTrigger>
                  <SelectContent>
                    {batches.map(batch => (
                      <SelectItem key={batch.batch_id} value={batch.batch_id}>
                        {batch.date} | {batch.start_time} - {batch.end_time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedCustomerData && (
              <div className="p-3 bg-[#E2E8E4] rounded-md text-sm">
                <p className="font-medium text-[#1C1C1A]">{selectedCustomerData.name}</p>
                <p className="text-[#1C1C1A]/60">{selectedCustomerData.phone_number}</p>
                <p className="text-[#1C1C1A]/60">Status: {selectedCustomerData.status}</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                data-testid="preview-button"
                onClick={handlePreview}
                disabled={loading}
                variant="outline"
                className="flex-1"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                data-testid="send-button"
                onClick={handleSend}
                disabled={loading}
                className="flex-1 bg-[#2C4C3B] hover:bg-[#2C4C3B]/90"
              >
                <PaperPlaneTilt className="w-4 h-4 mr-2" />
                Send
              </Button>
            </div>
          </div>
        </Card>

        {/* Preview Card */}
        <Card className="p-6 bg-white border-[#E5E5DF]" data-testid="preview-card">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-[#2C4C3B]" />
            <h2 className="text-xl font-medium text-[#1C1C1A]">Message Preview</h2>
          </div>

          {previewMessage ? (
            <div className="space-y-3">
              <div className="text-sm text-[#1C1C1A]/60">
                To: {previewPhone}
              </div>
              <div className="p-4 bg-[#E2E8E4] rounded-lg whitespace-pre-wrap text-sm text-[#1C1C1A]">
                {previewMessage}
              </div>
              <div className="text-xs text-[#1C1C1A]/50 italic">
                Note: This is a preview. To actually send via WhatsApp, integrate with a provider like Twilio, WATI, or Meta WhatsApp Business API.
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-[#1C1C1A]/40">
              <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Click &quot;Preview&quot; to see the message</p>
            </div>
          )}
        </Card>
      </div>

      {/* Batch Send Card */}
      <Card className="p-6 bg-white border-[#E5E5DF]" data-testid="batch-send-card">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-[#C05C42]" />
          <h2 className="text-xl font-medium text-[#1C1C1A]">Send to Entire Batch</h2>
        </div>

        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <Label>Select Batch</Label>
            <Select value={selectedBatch} onValueChange={setSelectedBatch}>
              <SelectTrigger data-testid="batch-send-select">
                <SelectValue placeholder="Select batch" />
              </SelectTrigger>
              <SelectContent>
                {batches.map(batch => (
                  <SelectItem key={batch.batch_id} value={batch.batch_id}>
                    {batch.date} | {batch.start_time} - {batch.end_time} ({batch.assigned_customers.length} customers)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="batch-send-button"
            onClick={handleBatchSend}
            disabled={loading || !selectedBatch}
            className="bg-[#C05C42] hover:bg-[#C05C42]/90"
          >
            <PaperPlaneTilt className="w-4 h-4 mr-2" />
            Send to All
          </Button>
        </div>
      </Card>

      {/* Notification History */}
      <Card className="p-6 bg-white border-[#E5E5DF]" data-testid="notification-history">
        <h2 className="text-xl font-medium text-[#1C1C1A] mb-4">Recent Notifications</h2>
        
        <div className="space-y-2">
          {notifications.length === 0 ? (
            <p className="text-center py-8 text-[#1C1C1A]/40">No notifications sent yet</p>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.notification_id}
                className="flex items-center justify-between p-3 border border-[#E5E5DF] rounded-md hover:bg-[#F9F9F8]"
              >
                <div className="flex items-center gap-3">
                  {notif.status === 'pending' ? (
                    <Clock className="w-5 h-5 text-yellow-600" />
                  ) : notif.status === 'sent' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <div>
                    <p className="font-medium text-[#1C1C1A]">{notif.phone_number}</p>
                    <p className="text-sm text-[#1C1C1A]/60">{notif.template_type.replace('_', ' ')}</p>
                  </div>
                </div>
                <div className="text-right text-sm text-[#1C1C1A]/60">
                  {new Date(notif.sent_at).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
    </Layout>
  );
};

export default Notifications;
